import React from 'react'
import * as PhosphorIcons from '@phosphor-icons/react'

const EmotionAnalyzerViewResult = ({result}) => {
  const emotionColors = {
    joy: { bg: 'bg-yellow-100', text: 'text-yellow-600', icon: 'Smiley' },
    sadness: { bg: 'bg-blue-100', text: 'text-blue-600', icon: 'SmileyMeh' },
    anger: { bg: 'bg-red-100', text: 'text-red-600', icon: 'SmileySad' },
    fear: { bg: 'bg-purple-100', text: 'text-purple-600', icon: 'SmileyNervous' },
    surprise: { bg: 'bg-green-100', text: 'text-green-600', icon: 'SmileyWink' },
    disgust: { bg: 'bg-orange-100', text: 'text-orange-600', icon: 'SmileyXEyes' },
    trust: { bg: 'bg-cyan-100', text: 'text-cyan-600', icon: 'Heart' }
  };

  const intensityColors = {
    low: 'bg-info/20 text-info',
    moderate: 'bg-warning/20 text-warning',
    high: 'bg-error/20 text-error'
  };

  return (
    <div className="mt-4 overflow-x-auto">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {/* Primary Emotion Analysis */}
        <div className="space-y-4">
          {/* Primary Emotion Card */}
          <div className="card bg-base-100 shadow-sm">
            <div className="card-body p-4">
              <div className="flex items-center gap-2 mb-4">
                <div className={`p-2 rounded-lg ${emotionColors[result?.emotions?.primary_emotion?.type]?.bg || 'bg-base-200'}`}>
                  {PhosphorIcons[emotionColors[result?.emotions?.primary_emotion?.type]?.icon] && 
                    React.createElement(PhosphorIcons[emotionColors[result?.emotions?.primary_emotion?.type]?.icon], {
                      size: 20,
                      weight: "bold",
                      className: emotionColors[result?.emotions?.primary_emotion?.type]?.text
                    })
                  }
                </div>
                <h3 className="card-title text-lg">Primary Emotion</h3>
              </div>

              <div className="space-y-3">
                <div className="flex items-center justify-between p-3 bg-base-200 rounded-lg">
                  <span className="font-medium capitalize">
                    {result?.emotions?.primary_emotion?.type}
                  </span>
                  <div className="flex gap-2">
                    <span className={`px-2 py-0.5 rounded-full text-xs ${intensityColors[result?.emotions?.primary_emotion?.intensity]}`}>
                      {result?.emotions?.primary_emotion?.intensity} intensity
                    </span>
                    <span className="px-2 py-0.5 rounded-full text-xs bg-success/20 text-success">
                      {Math.round(result?.emotions?.primary_emotion?.confidence * 100)}% confidence
                    </span>
                  </div>
                </div>

                {/* Emotion Triggers */}
                <div className="p-3 bg-base-200 rounded-lg">
                  <h4 className="font-medium mb-2">Triggers</h4>
                  <div className="flex flex-wrap gap-2">
                    {result?.emotions?.primary_emotion?.triggers?.map((trigger, index) => (
                      <span key={index} className="px-2 py-1 bg-base-300 rounded-full text-sm">
                        {trigger}
                      </span>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Secondary Emotions */}
          <div className="card bg-base-100 shadow-sm">
            <div className="card-body p-4">
              <div className="flex items-center gap-2 mb-4">
                <div className="p-2 rounded-lg bg-secondary/10">
                  <PhosphorIcons.Waves size={20} weight="bold" className="text-secondary"/>
                </div>
                <h3 className="card-title text-lg">Secondary Emotions</h3>
              </div>

              <div className="space-y-2">
                {result?.emotions?.secondary_emotions?.map((emotion, index) => (
                  <div key={index} className="p-3 bg-base-200 rounded-lg">
                    <div className="flex justify-between items-center">
                      <span className="font-medium capitalize">{emotion.type}</span>
                      <div className="flex gap-2">
                        <span className={`px-2 py-0.5 rounded-full text-xs ${intensityColors[emotion.intensity]}`}>
                          {emotion.intensity}
                        </span>
                        <span className="px-2 py-0.5 rounded-full text-xs bg-success/20 text-success">
                          {Math.round(emotion.confidence * 100)}%
                        </span>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>

        {/* Context and Analysis */}
        <div className="space-y-4">
          {/* Emotional Context */}
          <div className="card bg-base-100 shadow-sm">
            <div className="card-body p-4">
              <div className="flex items-center gap-2 mb-4">
                <div className="p-2 rounded-lg bg-primary/10">
                  <PhosphorIcons.Airplane size={20} weight="bold" className="text-primary"/>
                </div>
                <h3 className="card-title text-lg">Emotional Context</h3>
              </div>

              <div className="space-y-3">
                <div className="p-3 bg-base-200 rounded-lg">
                  <div className="flex justify-between items-center mb-2">
                    <span className="font-medium">Situation Type</span>
                    <span className="px-2 py-0.5 rounded-full text-xs bg-primary/20 text-primary capitalize">
                      {result?.context?.situation_type}
                    </span>
                  </div>
                </div>

                {/* Environmental Factors */}
                <div className="p-3 bg-base-200 rounded-lg">
                  <h4 className="font-medium mb-2">Environmental Factors</h4>
                  <div className="flex flex-wrap gap-2">
                    {result?.context?.environmental_factors?.map((factor, index) => (
                      <span key={index} className="px-2 py-1 bg-base-300 rounded-full text-sm">
                        {factor}
                      </span>
                    ))}
                  </div>
                </div>

                {/* Temporal Indicators */}
                <div className="p-3 bg-base-200 rounded-lg">
                  <h4 className="font-medium mb-2">Temporal Indicators</h4>
                  <div className="flex flex-wrap gap-2">
                    {result?.context?.temporal_indicators?.map((indicator, index) => (
                      <span key={index} className="px-2 py-1 bg-base-300 rounded-full text-sm">
                        {indicator}
                      </span>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Analysis Details */}
          <div className="card bg-base-100 shadow-sm">
            <div className="card-body p-4">
              <div className="flex items-center gap-2 mb-4">
                <div className="p-2 rounded-lg bg-info/10">
                  <PhosphorIcons.ChartBar size={20} weight="bold" className="text-info"/>
                </div>
                <h3 className="card-title text-lg">Analysis Details</h3>
              </div>

              <div className="space-y-3">
                <div className="p-3 bg-base-200 rounded-lg">
                  <div className="flex justify-between items-center">
                    <span className="font-medium">Emotional Complexity</span>
                    <span className="px-2 py-0.5 rounded-full text-xs bg-info/20 text-info capitalize">
                      {result?.analysis?.emotional_complexity}
                    </span>
                  </div>
                </div>

                {/* Key Expressions */}
                <div className="p-3 bg-base-200 rounded-lg">
                  <h4 className="font-medium mb-2">Key Expressions</h4>
                  <div className="space-y-1">
                    {result?.analysis?.key_expressions?.map((expression, index) => (
                      <div key={index} className="flex items-center gap-2 text-sm">
                        <PhosphorIcons.Quotes size={16} className="text-info"/>
                        <span>{expression}</span>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Behavioral Indicators */}
                <div className="p-3 bg-base-200 rounded-lg">
                  <h4 className="font-medium mb-2">Behavioral Indicators</h4>
                  <div className="flex flex-wrap gap-2">
                    {result?.analysis?.behavioral_indicators?.map((indicator, index) => (
                      <span key={index} className="px-2 py-1 bg-base-300 rounded-full text-sm">
                        {indicator}
                      </span>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default EmotionAnalyzerViewResult;